import i18n from '@/i18n'
import { getActiveLocale } from '@/lib/locale'

/**
 * Account-level currency display (symbol only — amounts are not converted).
 *
 * Module state lets non-React helpers (dashboard/report formatters) stay in sync
 * with the setting loaded by AccountRegionalProvider.
 */

export type CurrencyDisplay = 'BRL' | 'USD'

/** pt-BR text is the fallback; the keys are resolved by the component that renders them. */
export const CURRENCY_DISPLAY_OPTIONS: ReadonlyArray<{
  value: CurrencyDisplay
  label: string
  labelKey: string
  symbol: string
  description: string
  descriptionKey: string
}> = [
  {
    value: 'BRL',
    label: 'Real (R$)',
    labelKey: 'currency.brlLabel',
    symbol: 'R$',
    description: 'Formato brasileiro padrão',
    descriptionKey: 'currency.brlDescription',
  },
  {
    value: 'USD',
    label: 'Dólar / peso ($)',
    labelKey: 'currency.usdLabel',
    symbol: '$',
    description: 'Usa o símbolo $ sem converter valores',
    descriptionKey: 'currency.usdDescription',
  },
]

const DEFAULT_CURRENCY: CurrencyDisplay = 'BRL'

let activeCurrency: CurrencyDisplay = DEFAULT_CURRENCY
const listeners = new Set<() => void>()

export function isCurrencyDisplay(value: unknown): value is CurrencyDisplay {
  return value === 'BRL' || value === 'USD'
}

export function getCurrencyDisplay(): CurrencyDisplay {
  return activeCurrency
}

export function setCurrencyDisplay(next: CurrencyDisplay): void {
  if (next === activeCurrency) return
  activeCurrency = next
  listeners.forEach((listener) => listener())
}

export function subscribeCurrencyDisplay(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function getCurrencySymbol(display: CurrencyDisplay = activeCurrency): string {
  return display === 'USD' ? '$' : 'R$'
}

/** Digit grouping and decimal separator follow the interface language, not the currency. */
function formatAmount(
  value: number,
  options?: { minimumFractionDigits?: number; maximumFractionDigits?: number }
): string {
  return (value || 0).toLocaleString(getActiveLocale(), {
    minimumFractionDigits: options?.minimumFractionDigits ?? 2,
    maximumFractionDigits: options?.maximumFractionDigits ?? 2,
  })
}

/** Full currency string, e.g. "R$ 1.234,56" or "$ 1.234,56". */
export function formatAccountCurrency(
  value: number,
  display: CurrencyDisplay = activeCurrency
): string {
  return `${getCurrencySymbol(display)} ${formatAmount(value || 0)}`
}

/** Compact KPI form: "$ 1,5 mi" / "R$ 12,3 mil" / full under 10k. */
export function formatAccountCurrencyCompact(
  value: number,
  display: CurrencyDisplay = activeCurrency
): string {
  const amount = value || 0
  const abs = Math.abs(amount)
  const symbol = getCurrencySymbol(display)

  const t = i18n.getFixedT(null, 'common')

  if (abs >= 1_000_000) {
    return `${symbol} ${(amount / 1_000_000).toLocaleString(getActiveLocale(), { maximumFractionDigits: 1 })} ${t('currency.million')}`
  }
  if (abs >= 10_000) {
    return `${symbol} ${(amount / 1_000).toLocaleString(getActiveLocale(), { maximumFractionDigits: 1 })} ${t('currency.thousand')}`
  }

  return `${symbol} ${formatAmount(amount, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`
}
